/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  DataGridPro, useGridApiRef, gridFilteredSortedRowIdsSelector,
} from '@mui/x-data-grid-pro';
import AddIcon from '@mui/icons-material/Add';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { IconButton, Tooltip as MuiTooltip } from '@mui/material';
import Drawer from '@mui/material/Drawer';
import Backdrop from '@mui/material/Backdrop';
import ClipLoader from 'react-spinners/ClipLoader';
import { Tooltip } from 'react-tooltip';
import { TrackstarConnectButton } from '@trackstar/react-trackstar-link';
import { useFetch } from './api';
import HorizontalScrollText from './HorizontalScroll';
import CustomColumnMenu from './CustomColumnMenu';
import GenerateMagicLinkModal from './GenerateMagicLinkModal';
import ConnectionDetailDrawer from './ConnectionDetailDrawer';
import CopyButton from './CopyButton';
import EditableCustomerId from './EditableCustomerId';

// unified DataGrid styling with refined aesthetic
export const dataGridSx = {
  border: 'none',
  fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
  '--DataGrid-rowBorderColor': '#F5F5F4',
  '& .MuiDataGrid-columnHeader': {
    backgroundColor: '#FAFAF9',
  },
  '& .MuiDataGrid-columnHeaders': {
    borderBottom: '1px solid #E7E5E4',
    fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
    fontSize: '0.8125rem',
    fontWeight: 500,
    color: '#78716C',
    letterSpacing: '-0.01em',
    minHeight: '44px !important',
    maxHeight: '44px !important',
  },
  '& .MuiDataGrid-columnSeparator': {
    display: 'none',
  },
  '& .MuiDataGrid-cell': {
    fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
    fontSize: '0.8125rem',
    fontWeight: 400,
    color: '#44403C',
    // no cell border: the DataGrid v8 --DataGrid-rowBorderColor above already draws
    // the single row hairline; a cell borderBottom would stack a second line
    padding: '0 16px',
  },
  // the Customer ID editor is wider than its column, so let it spill left over
  // the neighbouring cells rather than be clipped
  '& .MuiDataGrid-cell[data-field="customer_id"]': {
    overflow: 'visible',
    position: 'relative',
    zIndex: 1,
    // the input draws its own focus ring; MUI's cell outline would box in the
    // columns the editor overlaps
    '&:focus, &:focus-within': {
      outline: 'none',
    },
  },
  '& .MuiDataGrid-row': {
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: '#FAFAF9',
      cursor: 'pointer',
    },
    '&.Mui-selected': {
      backgroundColor: '#F5F5F4',
    },
    '&.Mui-selected:hover': {
      backgroundColor: '#F5F5F4',
    },
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: '1px solid #E7E5E4',
    backgroundColor: '#FAFAF9',
    minHeight: '44px',
    maxHeight: '44px',
  },
  '& .MuiDataGrid-virtualScroller': {
    backgroundColor: '#FFFFFF',
  },
};

// status badge component
function StatusBadge({ status }) {
  const styles = {
    OK: 'badge-success',
    Warning: 'badge-warning',
    Errors: 'badge-error',
    Disabled: 'badge-neutral',
  };
  const dotStyles = {
    OK: 'status-dot-success',
    Warning: 'status-dot-warning',
    Errors: 'status-dot-error',
    Disabled: 'status-dot-neutral',
  };
  return (
    <span className={`badge ${styles[status]} w-[90px] shrink-0 justify-center`}>
      <span className={`status-dot ${dotStyles[status]}`} />
      {status === 'Warning' ? 'Warn' : status}
    </span>
  );
}

function Connections(props) {
  const trackstar = useFetch();
  const statusParam = new URLSearchParams(window.location.search).get('status');
  const validStatusFilters = ['All', 'OK', 'Warning', 'Errors', 'Disabled'];
  const [selectedGridFilter, setSelectedGridFilter] = useState(
    validStatusFilters.includes(statusParam) ? statusParam : 'All',
  );
  const [adjustedConnectionData, setAdjustedConnectionData] = useState(null);
  const [connectionRows, setConnectionRows] = useState(null);
  const [quickFilterSearchValue, setQuickFilterSearchValue] = useState('');
  const [installedIntegrationResponse, setInstalledIntegrationResponse] = useState(null);
  const [generateMagicLinkModalOpen, setGenerateMagicLinkModalOpen] = useState(false);
  const [showEditCustomerIdConfirmation, setShowEditCustomerIdConfirmation] = useState(false);
  const [showConnectionDetail, setShowConnectionDetail] = useState(false);
  const [connectionDetail, setConnectionDetail] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [showIntegrationTypeSelector, setShowIntegrationTypeSelector] = useState(false);

  const location = useLocation();
  const gridApiRef = useGridApiRef();

  const {
    fetchConnectionData,
    connectionData,
    loadingConnectionData,
    connectionIssues,
    integrationLists,
    integrationListsLoading,
  } = props;

  const handleEditCustomerId = (connectionId, customerId) => trackstar(
    `internal/connections/${connectionId}`,
    'PATCH',
    { customer_id: customerId },
  )
    .then((response) => {
      // useFetch resolves on 4xx/5xx, so a rejected save only surfaces as an
      // error body; rethrow it so the editor can report the failure
      if (response?.error) throw new Error(response.error);
      // returned so the editor closes on fresh rows rather than flashing the old value
      return fetchConnectionData();
    })
    .then(() => {
      setShowEditCustomerIdConfirmation(true);
      setTimeout(() => setShowEditCustomerIdConfirmation(false), 3000);
    });

  const handleCopyConnectionId = (connectionId) => {
    navigator.clipboard.writeText(connectionId);
    setCopiedId(connectionId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getTrackstarConnectButton = (buttonBody, styling, connectionId, integrationType) => {
    const postBody = {};
    if (connectionId) {
      postBody.connection_id = connectionId;
    }
    const buttonId = connectionId ? `reinstall-connection-${connectionId}` : `add-new-connection-${integrationType || 'default'}`;
    return (
      <TrackstarConnectButton
        buttonId={buttonId}
        integrationType={integrationType}
        getLinkToken={async () => {
          const linkToken = await trackstar('internal/link/token', 'POST', postBody).then((response) => response.link_token);
          return linkToken;
        }}
        onSuccess={async (authCode) => {
          await trackstar('internal/link/exchange', 'POST', { auth_code: authCode }).then((response) => {
            fetchConnectionData();
            setInstalledIntegrationResponse(response);
            setShowConnectionDetail(false);
            return response;
          });
        }}
        onClose={() => {}}
        onLoad={() => {}}
        style={styling}
      >
        {buttonBody}
      </TrackstarConnectButton>
    );
  };

  const cleanupUrl = () => {
    const currentPath = window.location.pathname;
    window.history.pushState({}, '', currentPath);
  };

  const openConnectionDetail = (row) => {
    const connectionId = row.connection_id;
    setConnectionDetail(row);
    setShowConnectionDetail(true);
    // if the url already points at this connection (arrived via a ?id= link, or
    // browser Back landed here) don't push another entry — that dup makes Back stick
    if (new URLSearchParams(window.location.search).get('id') === connectionId) return;
    const currentPath = window.location.pathname;
    const currentSearch = window.location.search;
    const newSearch = new URLSearchParams(currentSearch);
    newSearch.set('id', connectionId);
    window.history.pushState({}, '', `${currentPath}?${newSearch.toString()}`);
  };

  // keep an ?modal= param on the url so the magic-link modal is shareable and
  // can be opened from the command palette
  const setModalParam = (value) => {
    const params = new URLSearchParams(window.location.search);
    if (value) {
      params.set('modal', value);
    } else {
      params.delete('modal');
    }
    const qs = params.toString();
    const target = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    window.history.pushState({}, '', target);
  };

  const openMagicLinkModal = () => {
    setGenerateMagicLinkModalOpen(true);
    setModalParam('magic-link');
  };

  const closeMagicLinkModal = () => {
    setGenerateMagicLinkModalOpen(false);
    setModalParam(null);
  };

  const colHeaders = [
    {
      field: 'connection_id',
      headerName: 'Connection ID',
      display: 'flex',
      flex: 1.5,
      minWidth: 280,
      renderCell: (params) => (
        <div className="flex items-center gap-2">
          <MuiTooltip title={copiedId === params.value ? 'Copied!' : 'Copy'} arrow placement="top">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleCopyConnectionId(params.value);
              }}
              className="text-surface-400 hover:text-surface-600 hover:bg-surface-100"
              sx={{ padding: '4px' }}
            >
              <ContentCopyIcon sx={{ fontSize: '14px' }} />
            </IconButton>
          </MuiTooltip>
          <span className="font-mono text-xs text-surface-600">{params.value}</span>
        </div>
      ),
    },
    {
      field: 'integration_name',
      headerName: 'Integration',
      display: 'flex',
      flex: 1,
      minWidth: 160,
      renderCell: (params) => {
        const { value } = params;
        const typeLabels = {
          wms: 'WMS', cart: 'Cart', carrier: 'Carrier', freight: 'Freight', erp: 'ERP',
        };
        const typeLabel = typeLabels[params.row.integration_type] || params.row.integration_type;
        let logo = `https://trackstarlogosbucket.s3.amazonaws.com/${value}.png`;
        if (value === 'sandbox') {
          logo = 'ts_logo_2024.png';
        }
        return (
          <div className="flex gap-2.5 items-center">
            <div className="w-7 h-7 rounded-md bg-surface-100 flex items-center justify-center border border-surface-200">
              <img src={logo} alt={`${value} logo`} className="h-4 w-4" />
            </div>
            <div className="flex flex-col">
              <span className="text-surface-700 font-medium">{params.row.displayName}</span>
              <span className="text-[11px] text-surface-400">{typeLabel}</span>
            </div>
          </div>
        );
      },
    },
    {
      field: 'created_at',
      renderHeader: () => (
        <div className="flex items-center gap-1.5">
          <span>Created</span>
          <img id="created-date-tip" src="infov2.png" alt="info" className="h-3.5 w-3.5 opacity-40" />
        </div>
      ),
      flex: 0.8,
      minWidth: 140,
      valueFormatter: (value) => {
        const date = new Date(value);
        return date.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
      },
    },
    {
      field: 'times_used',
      flex: 0.5,
      minWidth: 100,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      valueFormatter: (value) => {
        if (value === null) return 0;
        return value.toLocaleString('en-US');
      },
      renderHeader: () => (
        <div className="flex items-center gap-1.5">
          <span>API Calls</span>
          <img id="api-calls-tip" src="infov2.png" alt="info" className="h-3.5 w-3.5 opacity-40" />
        </div>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      display: 'flex',
      flex: 0.5,
      minWidth: 100,
      renderHeader: () => (
        <div className="flex items-center gap-1.5">
          <span>Status</span>
          <img id="status-tip" src="infov2.png" alt="info" className="h-3.5 w-3.5 opacity-40" />
        </div>
      ),
      renderCell: (params) => <StatusBadge status={params.value} />,
    },
    {
      field: 'customer_id',
      display: 'flex',
      headerAlign: 'right',
      align: 'right',
      flex: 1,
      minWidth: 140,
      renderHeader: () => (
        <div className="flex items-center gap-1.5">
          <span>Customer ID</span>
          <img id="customer-id-tip" src="infov2.png" alt="info" className="h-3.5 w-3.5 opacity-40" />
        </div>
      ),
      renderCell: (params) => (
        <div className="flex w-full justify-end gap-1 items-center min-w-0">
          <EditableCustomerId
            value={params.value}
            onSave={(customerId) => handleEditCustomerId(params.row.connection_id, customerId)}
            inputClassName="w-44"
            editorAlign="right"
          />
          <CopyButton value={params.value} label="Copy Customer ID" />
        </div>
      ),
    },
    // hidden column so quick filter searches display names (e.g. "amazon" matches "fba")
    {
      field: 'displayName',
      headerName: 'Display Name',
    },
  ];

  const updateSearchValue = (newValue) => setTimeout(() => {
    gridApiRef.current.setQuickFilterValues(
      newValue.split(' ').filter((word) => word !== ''),
    );
  }, 300);

  const handleQuickFilterSearchChange = (event) => {
    if (event.target.value !== undefined) {
      setQuickFilterSearchValue(event.target.value);
      updateSearchValue(event.target.value);
    }
  };

  useEffect(() => {
    if (connectionData && integrationLists && !integrationListsLoading) {
      const adjustedData = [];
      connectionData.forEach((conn) => {
        const connection = conn;
        const status = (() => {
          // a fully-paused connection reads as Disabled regardless of errors
          if (conn.paused) return 'Disabled';
          if (!conn.errors || conn.errors.length === 0) return 'OK';
          const hasBlockingError = conn.errors.some(
            (err) => err.affected_endpoints?.some((ep) => ep.preventing_sync),
          );
          return hasBlockingError ? 'Errors' : 'Warning';
        })();
        const integrationType = conn.integration_type;
        const integrationTypeList = integrationLists[integrationType];
        const intEntry = integrationTypeList[conn.integration_name];
        let displayName = conn.integration_name;
        if (intEntry) {
          displayName = intEntry.display_name;
        }
        adjustedData.push({
          id: connection.connection_id, status, displayName, ...connection,
        });
      });
      setAdjustedConnectionData(adjustedData);
      setConnectionRows(adjustedData);
      if (connectionDetail) {
        const newConnectionDetail = adjustedData.find(
          (conn) => conn.connection_id === connectionDetail.connection_id,
        );
        setConnectionDetail(newConnectionDetail);
      }
    }
  }, [connectionData, integrationLists, integrationListsLoading]);

  useEffect(() => {
    const rows = [];
    if (!adjustedConnectionData) return;
    adjustedConnectionData.forEach((connection) => {
      const row = { ...connection };
      if (row.times_used === null) row.times_used = 0;
      if (selectedGridFilter !== 'All' && row.status !== selectedGridFilter) return;
      rows.push(row);
    });
    setConnectionRows(rows);
  }, [selectedGridFilter, adjustedConnectionData]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!showConnectionDetail) return;

      if (event.key === 'Escape') {
        setShowConnectionDetail(false);
        cleanupUrl();
        return;
      }

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        // get visible rows only (respects both sorting and filtering)
        const sortedRowIds = gridFilteredSortedRowIdsSelector(gridApiRef);
        const currentIndex = sortedRowIds.findIndex(
          (id) => id === connectionDetail?.connection_id,
        );
        if (currentIndex === -1) return;

        const newIndex = event.key === 'ArrowDown'
          ? Math.min(currentIndex + 1, sortedRowIds.length - 1)
          : Math.max(currentIndex - 1, 0);

        if (newIndex !== currentIndex) {
          const newRowId = sortedRowIds[newIndex];
          const newRow = connectionRows.find((r) => r.connection_id === newRowId);
          if (!newRow) return;

          openConnectionDetail(newRow);
          // scroll row into view
          gridApiRef.current.scrollToIndexes({ rowIndex: newIndex });
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showConnectionDetail, connectionRows, connectionDetail, gridApiRef]);

  // key on location too so navigating to ?id= while already on /connections
  // (e.g. from the command palette) opens the drawer, not just a fresh mount
  useEffect(() => {
    const connectionId = new URLSearchParams(location.search).get('id');
    if (connectionId && connectionRows) {
      const connection = connectionRows.find((conn) => conn.connection_id === connectionId);
      if (connection) openConnectionDetail(connection);
    }
  }, [connectionRows, location]);

  // sync the magic-link modal to the ?modal= param so palette navigation and
  // browser back/forward open and close it
  useEffect(() => {
    const modal = new URLSearchParams(location.search).get('modal');
    setGenerateMagicLinkModalOpen(modal === 'magic-link');
  }, [location]);

  const getDetailPanelHeight = useCallback(() => 200, []);

  return (
    <div className="page-container">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-baseline gap-3">
            <h1 className="page-title">Connections</h1>
            <span className="page-subtitle">Manage your linked integrations</span>
          </div>
          {(loadingConnectionData || integrationListsLoading) ? (
            <div className="mt-1">
              <ClipLoader size={14} color="#78716C" />
            </div>
          ) : (
            <p className="text-sm text-surface-500 mt-1">
              <span className="font-semibold text-surface-800">{adjustedConnectionData ? adjustedConnectionData.length : 0}</span>
              {' total'}
              <span className="mx-2 text-surface-300">•</span>
              <span className={`font-semibold ${connectionIssues > 0 ? 'text-warning-dark' : 'text-surface-800'}`}>{connectionIssues}</span>
              {` ${connectionIssues === 1 ? 'issue' : 'issues'}`}
            </p>
          )}
        </div>

        {showEditCustomerIdConfirmation && (
          <div className="flex items-center px-4 py-2 bg-success-light border border-emerald-200 rounded-lg">
            <p className="text-sm font-medium text-success-dark">Customer ID updated</p>
          </div>
        )}

        <div className="flex gap-2">
          <div className="relative">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setShowIntegrationTypeSelector(!showIntegrationTypeSelector)}
            >
              <AddIcon sx={{ fontSize: '18px' }} />
              <span>Add connection</span>
            </button>
            {showIntegrationTypeSelector && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowIntegrationTypeSelector(false)}
                  aria-hidden="true"
                />
                <div className="absolute top-full right-0 mt-2 w-64 bg-surface-0 rounded-xl border border-surface-200 shadow-2xl z-50 overflow-hidden">
                  <div className="px-4 py-3 border-b border-surface-100 bg-surface-50">
                    <p className="text-xs font-semibold text-surface-500 uppercase tracking-wider">Select Connection Type</p>
                  </div>
                  <div className="py-2">
                    {[
                      { type: 'wms', label: 'WMS/3PL' },
                      { type: 'cart', label: 'Cart' },
                      { type: 'carrier', label: 'Carrier' },
                      { type: 'erp', label: 'ERP', alpha: true },
                    ].map(({
                      type, label, alpha,
                    }, index, arr) => (
                      <div
                        key={type}
                        onClick={() => setShowIntegrationTypeSelector(false)}
                        role="presentation"
                        className={`hover:bg-surface-50 transition-colors ${index < arr.length - 1 ? 'border-b border-surface-100' : ''}`}
                      >
                        {getTrackstarConnectButton(
                          <span className="inline-flex items-center gap-1.5 font-medium">
                            {label}
                            {alpha && (
                              <span className="text-[9px] font-semibold leading-none px-1 py-0.5 rounded-full bg-warning-light text-warning-dark uppercase">
                                Alpha
                              </span>
                            )}
                          </span>,
                          {
                            width: '100%',
                            textAlign: 'left',
                            padding: '12px 16px',
                            fontSize: '14px',
                            color: '#1C1917',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
                          },
                          null,
                          type,
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={openMagicLinkModal}
          >
            <AutoFixHighIcon sx={{ fontSize: '18px' }} />
            <span>Magic Link</span>
          </button>
        </div>
      </div>

      {/* Success notification */}
      {installedIntegrationResponse && (
        <div className="border border-success bg-success-light/50 rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-surface-800">Connection installed successfully</h2>
            <button
              type="button"
              onClick={() => setInstalledIntegrationResponse(null)}
              className="btn btn-secondary text-xs py-1.5 px-3"
            >
              Close
            </button>
          </div>
          <p className="text-sm text-surface-600 mb-4">Copy your access token and store it securely</p>
          <div className="bg-surface-0 rounded-lg border border-surface-200 overflow-hidden">
            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b border-surface-100">
                  <th scope="row" className="px-4 py-3 text-surface-500 font-medium w-36 text-left">Integration</th>
                  <td className="px-4 py-3 text-surface-700">
                    <HorizontalScrollText content={installedIntegrationResponse.integration_name} />
                  </td>
                </tr>
                <tr className="border-b border-surface-100">
                  <th scope="row" className="px-4 py-3 text-surface-500 font-medium text-left">Access Token</th>
                  <td className="px-4 py-3 text-surface-700 font-mono text-xs">
                    <HorizontalScrollText content={installedIntegrationResponse.access_token} />
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="px-4 py-3 text-surface-500 font-medium text-left">Connection ID</th>
                  <td className="px-4 py-3 text-surface-700 font-mono text-xs">
                    <HorizontalScrollText content={installedIntegrationResponse.connection_id} />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Data table section */}
      <div className="flex flex-col flex-1 overflow-hidden rounded-xl border border-surface-200 bg-surface-0 shadow-xs">
        {/* Table header */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-surface-50/50 border-b border-surface-200">
          <input
            type="text"
            placeholder="Search connections..."
            className="input w-56 !py-1.5 !text-sm"
            value={quickFilterSearchValue}
            onChange={handleQuickFilterSearchChange}
          />
          <div className="flex gap-2 items-center">
            <select
              value={selectedGridFilter}
              onChange={(e) => {
                const { value } = e.target;
                setSelectedGridFilter(value);
                const params = new URLSearchParams(window.location.search);
                if (value && value !== 'All') params.set('status', value);
                else params.delete('status');
                const qs = params.toString();
                window.history.replaceState({}, '', qs ? `${window.location.pathname}?${qs}` : window.location.pathname);
              }}
              className="input w-32 !py-1.5 !text-sm cursor-pointer"
            >
              <option value="All">All Statuses</option>
              <option value="OK">OK</option>
              <option value="Warning">Warn</option>
              <option value="Errors">Errors</option>
              <option value="Disabled">Disabled</option>
            </select>
          </div>
        </div>

        {/* DataGrid */}
        <div className="flex-1 min-h-0">
          <DataGridPro
            rows={connectionRows || []}
            columns={colHeaders}
            disableColumnMenu
            slots={{ columnMenu: CustomColumnMenu }}
            hideFooter
            rowHeight={48}
            sx={{ ...dataGridSx, height: '100%' }}
            initialState={{
              sorting: {
                sortModel: [{ field: 'created_at', sort: 'desc' }],
              },
              columns: {
                columnVisibilityModel: { displayName: false },
              },
            }}
            loading={loadingConnectionData || !connectionRows || integrationListsLoading}
            getDetailPanelHeight={getDetailPanelHeight}
            apiRef={gridApiRef}
            onRowClick={(params) => { openConnectionDetail(params.row); }}
            rowSelectionModel={{ type: 'include', ids: new Set(connectionDetail ? [connectionDetail.connection_id] : []) }}
            hideFooterSelectedRowCount
          />
        </div>
      </div>

      {/* Modals */}
      <GenerateMagicLinkModal
        isOpen={generateMagicLinkModalOpen}
        onClose={closeMagicLinkModal}
        connectionRows={connectionRows}
        integrationLists={integrationLists}
        integrationListsLoading={integrationListsLoading}
      />

      {/* Tooltips */}
      <Tooltip anchorSelect="#created-date-tip" content="The date this connection was created." />
      <Tooltip anchorSelect="#customer-id-tip" content="Your customer identifier for this connection." />
      <Tooltip anchorSelect="#api-calls-tip" content="Total API calls made with this connection." />
      <Tooltip anchorSelect="#status-tip" content="OK: No issues. Warn: Some data may be missing. Errors: Syncs cannot complete. Disabled: All syncs paused." />

      {/* Drawer backdrop and drawer */}
      <Backdrop
        open={showConnectionDetail}
        onClick={() => {
          setShowConnectionDetail(false);
          cleanupUrl();
        }}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer - 1,
          backgroundColor: 'rgba(12, 10, 9, 0.4)',
          backdropFilter: 'blur(4px)',
        }}
      />
      <Drawer
        anchor="right"
        open={showConnectionDetail}
        onClose={() => {
          setShowConnectionDetail(false);
          cleanupUrl();
        }}
        PaperProps={{
          sx: {
            width: '55%',
            maxWidth: '800px',
            backgroundColor: '#FAFAF9',
            borderLeft: '1px solid #E7E5E4',
            boxShadow: '-20px 0 40px -15px rgba(0, 0, 0, 0.1)',
          },
        }}
        variant="persistent"
      >
        <ConnectionDetailDrawer
          showConnectionDetail={showConnectionDetail}
          connectionDetail={connectionDetail}
          fetchConnectionData={fetchConnectionData}
          closeDrawer={() => {
            setShowConnectionDetail(false);
            cleanupUrl();
          }}
          onReinstallSuccess={(response) => {
            setShowConnectionDetail(false);
            setInstalledIntegrationResponse(response);
            fetchConnectionData();
          }}
        />
      </Drawer>
    </div>
  );
}

export default Connections;
